import PasswordField from '@/components/inputs/PasswordField/PasswordField';
import { capture } from '@/handlers/error';
import { useAuth } from '@/providers/auth/AuthProvider';
import { useSession } from '@/providers/session/SessionProvider';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import { PageHeader, useNotifications } from '@toolpad/core';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { AccountCard } from './components/AccountCard';

function Info(props: { label: string; value?: string }) {
    return (
        <Box>
            <Typography color='text.secondary' fontWeight={500}>
                {props.label}
            </Typography>
            <Typography>{props.value}</Typography>
        </Box>
    );
}

function PersonalInformation() {
    const { session } = useSession();
    const { t, i18n } = useTranslation();

    return (
        <AccountCard>
            <Stack spacing={4}>
                <Typography component='h5' sx={{ width: '100%', fontSize: 20 }}>
                    {t('account.personalInformation.title')}
                </Typography>
                <Stack spacing={2}>
                    <Info label={t('account.personalInformation.name')} value={session?.user?.name ?? undefined} />
                    <Info label={t('account.personalInformation.email')} value={session?.user?.email ?? undefined} />
                    <Info
                        label={t('account.personalInformation.role')}
                        value={session?.user?.role ? (i18n.exists(`common.role.${session.user.role}`) ? t(`common.role.${session.user.role}`) : session.user.role) : undefined}
                    />
                </Stack>
            </Stack>
        </AccountCard>
    );
}

function ChangePassword(props: { email: string; onChangePassword?: (currentPassword: string, newPassword: string) => Promise<boolean> }) {
    const notifications = useNotifications();
    const { t } = useTranslation();
    const [currentPassword, setCurrentPassword] = React.useState('');
    const [newPassword, setNewPassword] = React.useState('');
    const [newPasswordError, setNewPasswordError] = React.useState(false);
    const [newPasswordErrorMessage, setNewPasswordErrorMessage] = React.useState('');
    const [changingPassword, setChangingPassword] = React.useState(false);

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();

        if (newPasswordError) {
            return;
        }

        const data = new FormData(event.currentTarget);
        const currentPassword = data.get('current-password') as string;
        const newPassword = data.get('new-password') as string;

        if (props.onChangePassword) {
            setChangingPassword(true);
            await props
                .onChangePassword(currentPassword, newPassword)
                .then((success) => {
                    if (success) {
                        notifications.show(t('account.changePassword.success'), { severity: 'success', autoHideDuration: 5000 });
                    } else {
                        notifications.show(t('account.changePassword.failure'), { severity: 'error', autoHideDuration: 5000 });
                    }
                })
                .then(() => {
                    setCurrentPassword('');
                    setNewPassword('');
                })
                .catch((error) => {
                    notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
                    capture(error);
                })
                .finally(() => {
                    setChangingPassword(false);
                });
        }
    };

    const validateInputs = () => {
        const password = document.getElementById('new-password') as HTMLInputElement;

        let isValid = true;

        if (!password.value || password.value.length < 8) {
            setNewPasswordError(true);
            setNewPasswordErrorMessage(t('common.form.invalidPassword'));
            isValid = false;
        } else {
            setNewPasswordError(false);
            setNewPasswordErrorMessage('');
        }

        return isValid;
    };

    return (
        <AccountCard>
            <Stack spacing={4}>
                <Typography component='h5' sx={{ width: '100%', fontSize: 20 }}>
                    {t('account.changePassword.title')}
                </Typography>
                <Box
                    component='form'
                    onSubmit={handleSubmit}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        gap: 2,
                    }}
                >
                    {/* This hidden field is mainly for 3rd party software, e.g. password managers, to associated the web site info */}
                    <TextField id='email' name='email' type='email' autoComplete='email' value={props.email} sx={{ display: 'none' }} />
                    <PasswordField
                        name='current-password'
                        id='current-password'
                        autoComplete='current-password'
                        label={t('account.changePassword.currentPassword')}
                        required
                        value={currentPassword}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            setCurrentPassword(event.target.value);
                        }}
                    />
                    <PasswordField
                        name='new-password'
                        id='new-password'
                        autoComplete='new-password'
                        label={t('account.changePassword.newPassword')}
                        required
                        fullWidth
                        color={newPasswordError ? 'error' : 'primary'}
                        error={newPasswordError}
                        helperText={newPasswordErrorMessage}
                        value={newPassword}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            setNewPassword(event.target.value);
                        }}
                    />
                    <Button type='submit' loading={changingPassword} onClick={validateInputs} variant='contained' sx={{ mt: 2 }} loadingIndicator={<CircularProgress size={16} />}>
                        {t('account.changePassword.changePassword')}
                    </Button>
                </Box>
            </Stack>
        </AccountCard>
    );
}

export default function AccountPage() {
    const { t } = useTranslation();
    const { session } = useSession();
    const { updatePassword } = useAuth();
    const [value, setValue] = React.useState('general');

    const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
        setValue(newValue);
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, padding: 16 }}>
            <PageHeader title={t('account.title')} breadcrumbs={[]} />
            <Box sx={{ width: '100%', typography: 'body1', mt: 2 }}>
                <TabContext value={value}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <TabList onChange={handleChange} aria-label='account'>
                            <Tab label={t('account.tab.general')} value='general' />
                        </TabList>
                    </Box>
                    <TabPanel value='general' sx={{ pl: 0, pr: 0, pt: 6, pb: 6 }}>
                        <Stack spacing={3}>
                            <PersonalInformation />
                            <ChangePassword
                                email={session?.user?.email ?? ''}
                                onChangePassword={async (currentPassword, newPassword) => {
                                    if (!session?.token) {
                                        throw new Error('Session token is missing.');
                                    }

                                    return await updatePassword(session.token, currentPassword, newPassword);
                                }}
                            />
                        </Stack>
                    </TabPanel>
                    <TabPanel value='subscription'>TBD</TabPanel>
                </TabContext>
            </Box>
        </div>
    );
}
